import { useCallback, useState } from 'react'

import { RequestLog } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { CopyButton } from '#components/CopyButton.js'
import { CopyServiceRequestButton } from '#components/CopyServiceRequestButton.js'
import {
  RequestLogFilters,
  useRequestLogFilters,
} from '#components/RequestLogFilters.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { DocumentLookupDetails } from './DocumentLookupDetails'
import { DocumentLookupsList } from './DocumentLookupsList'
import { FetchDocumentsButton } from './FetchDocumentsButton'

const DocumentLookupsView = () => {
  const [selectedDocument, setSelectedDocument] = useState<RequestLog | null>(
    null,
  )
  const { filters, localFilters, setLocalFilters } = useRequestLogFilters()

  const {
    items: documents,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<RequestLog>('request-logs', {
    type: 'document',
    ...filters,
    limit: 50,
  })

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const handleDocumentSelect = useCallback((document: RequestLog) => {
    setSelectedDocument(document)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedDocument(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Document Lookup Details</span>
      {selectedDocument && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?requestId=${selectedDocument.requestId}`}
          />
          {selectedDocument.tenantRequest?.content && (
            <>
              <CopyServiceRequestButton log={selectedDocument} />
              <FetchDocumentsButton
                tenantRequestContent={{
                  ...selectedDocument.tenantRequest.content,
                  requestId: selectedDocument.requestId,
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="Document Lookups"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No document lookups found"
        filters={
          <RequestLogFilters
            isLoading={isLoading}
            onRefresh={refresh}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            errorTypes={[
              'authentication',
              'duplicate',
              'external',
              'internal',
              'unknown',
              'validation',
            ]}
            statuses={['ok', 'not-found', 'error']}
            showTrackingNumber
          />
        }
        listContent={
          documents.length > 0 ? (
            <DocumentLookupsList
              documents={documents}
              selectedId={selectedDocument?._id}
              onSelect={handleDocumentSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedDocument}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <DocumentLookupDetails document={selectedDocument} />
      </Slider>
    </>
  )
}

export { DocumentLookupsView }
